import { api } from "./client";

export type ForumStatus =
  | "SUCCESS"
  | "PARTIAL"
  | "FAILED"
  | "RUNNING"
  | "COOLDOWN"
  | "BLOCKED"
  | "DISABLED"
  | "NOT_RUN"
  | "STALE";

export interface ForumItem {
  forum: string;
  network: "tor" | "clearnet" | string;
  plugin: string;
  base_url?: string | null;
  status: ForumStatus;
  enabled: boolean;
  added?: string | null;
  last_run?: string | null;
  last_success?: string | null;
  last_failure?: string | null;
  duration?: number | null;
  pages?: number;
  urls_discovered?: number;
  jobs?: number;
  jobs_succeeded?: number;
  jobs_failed?: number;
  jobs_retried?: number;
  jobs_dlq?: number;
  new_leaks?: number;
  new_leaks_run?: number;
  new_leaks_24h?: number;
  total_leaks?: number;
  errors?: number;
  freshness_seconds?: number;
}

export interface ForumHealth {
  total_leaks?: number;
  new_leaks_24h?: number;
  success_runs: number;
  partial_runs: number;
  failed_runs: number;
  success_rate: number;
  average_duration_seconds: number;
  average_jobs_per_run: number;
  average_new_leaks_per_run: number;
  retry_count: number;
  dlq_count: number;
  dlq_backlog?: number;
  historical_dlq?: number;
  latest_run_dlq?: number;
  unresolved_errors: number;
}

export interface ForumDetail extends ForumItem {
  schedule_seconds?: number;
  runs?: RunItem[];
  health?: ForumHealth;
}

export interface RunItem {
  _id?: string;
  run_id: string;
  status: string;
  run_status?: string;
  data_status?: string;
  duration_seconds?: number;
  run_started_at?: string;
  run_finished_at?: string;
  finished_at?: string;
  pages_discovered?: number;
  pages_processed?: number;
  jobs_queued?: number;
  jobs_started?: number;
  jobs_succeeded?: number;
  jobs_failed?: number;
  jobs_retried?: number;
  jobs_dlq?: number;
  new_records?: number;
  records_stored?: number;
  duplicate_records?: number;
  new_urls?: number;
  urls_discovered?: number;
  urls_found?: number;
  failure_message?: string | null;
  failure_type?: string | null;
  triggered_by?: string;
  network?: string;
  forum?: string;
  warnings?: string[];
  updated_at?: string;
}

export interface ErrorItem {
  _id?: string;
  error_id: string;
  error_type: string;
  message: string;
  status?: string;
  resolution_status?: "OPEN" | "ACKNOWLEDGED" | "RESOLVED" | string;
  severity?: string;
  stage?: string;
  last_seen?: string;
  count?: number;
  stack_trace?: string;
  resolved?: boolean;
  resolved_at?: string;
  resolution_note?: string;
  acknowledged?: boolean;
  acknowledged_at?: string;
  run_id?: string;
  forum?: string;
}

export interface NoteItem {
  _id: string;
  forum: string;
  text: string;
  pinned: boolean;
  author?: { id?: string; email?: string };
  created_at?: string;
  updated_at?: string;
}

export interface ActivityItem {
  type: "run" | "error" | "note" | "note_edited" | "action" | "audit";
  timestamp?: string;
  action?: string;
  status?: string;
  message?: string;
  run_id?: string;
  error_id?: string;
  note_id?: string;
  request_id?: string;
}

export interface SummaryData {
  total_forums: number;
  status_counts: Record<string, number>;
  runs_today: number;
  errors_today: number;
  unresolved_errors: number;
  stale_forums: number;
  never_successfully_scraped: number;
}

export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

export const forumMonitoring = {
  summary: (): Promise<SummaryData> =>
    api.get<SummaryData>("/admin/forum-monitoring/summary").then((r) => r.data),

  forums: (params: Record<string, unknown>): Promise<PaginatedResult<ForumItem>> =>
    api.get<PaginatedResult<ForumItem>>("/admin/forum-monitoring/forums", { params }).then((r) => r.data),

  forum: (forum: string): Promise<ForumDetail> =>
    api.get<ForumDetail>(`/admin/forum-monitoring/forums/${encodeURIComponent(forum)}`).then((r) => r.data),

  runs: (forum: string, params: Record<string, unknown> = {}): Promise<PaginatedResult<RunItem>> =>
    api.get<PaginatedResult<RunItem>>(`/admin/forum-monitoring/forums/${encodeURIComponent(forum)}/runs`, { params }).then((r) => r.data),

  getRun: (runId: string): Promise<RunItem> =>
    api.get<RunItem>(`/admin/forum-monitoring/runs/${encodeURIComponent(runId)}`).then((r) => r.data),

  errors: (forum: string, params: Record<string, unknown> = {}): Promise<PaginatedResult<ErrorItem>> =>
    api.get<PaginatedResult<ErrorItem>>(`/admin/forum-monitoring/forums/${encodeURIComponent(forum)}/errors`, { params }).then((r) => r.data),

  error: (id: string): Promise<ErrorItem> =>
    api.get<ErrorItem>(`/admin/forum-monitoring/errors/${encodeURIComponent(id)}`).then((r) => r.data),

  notes: (forum: string): Promise<NoteItem[]> =>
    api.get<NoteItem[]>(`/admin/forum-monitoring/forums/${encodeURIComponent(forum)}/notes`).then((r) => r.data),

  addNote: (forum: string, text: string, pinned: boolean): Promise<NoteItem> =>
    api.post<NoteItem>(`/admin/forum-monitoring/forums/${encodeURIComponent(forum)}/notes`, { text, pinned }).then((r) => r.data),

  updateNote: (forum: string, id: string, text: string, pinned: boolean): Promise<NoteItem> =>
    api.patch<NoteItem>(`/admin/forum-monitoring/forums/${encodeURIComponent(forum)}/notes/${id}`, { text, pinned }).then((r) => r.data),

  deleteNote: (forum: string, id: string): Promise<{ deleted: boolean }> =>
    api.delete<{ deleted: boolean }>(`/admin/forum-monitoring/forums/${encodeURIComponent(forum)}/notes/${id}`).then((r) => r.data),

  activity: (forum: string, params: Record<string, unknown> = {}): Promise<PaginatedResult<ActivityItem>> =>
    api.get<PaginatedResult<ActivityItem>>(`/admin/forum-monitoring/forums/${encodeURIComponent(forum)}/activity`, { params }).then((r) => r.data),

  action: (
    forum: string,
    action: "RUN_NOW" | "CLEAR_COOLDOWN" | "ENABLE" | "DISABLE"
  ): Promise<{ request_id: string; forum: string; action: string; status: string; accepted: boolean }> =>
    api.post(`/admin/forum-monitoring/forums/${encodeURIComponent(forum)}/actions`, { action }).then((r) => r.data),

  actionStatus: (id: string): Promise<{ _id: string; forum: string; action: string; status: string; message?: string }> =>
    api.get(`/admin/forum-monitoring/actions/${id}`).then((r) => r.data),

  resolveError: (id: string, status: "ACKNOWLEDGED" | "RESOLVED", resolution_note = ""): Promise<ErrorItem> =>
    api.patch<ErrorItem>(`/admin/forum-monitoring/errors/${id}`, { status, resolution_note }).then((r) => r.data),
};

